"use memo"

import { TaskCategoryTaskType } from '@urbanx/agx.activities.client'
import {
  CoolingOffDetails,
  CoolingOffType,
  Condition,
  Deposit,
  DepositType,
  NoCoolingOffReason,
  SpecialConditionData,
  TimeUnit,
  ValueType,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetSpecialConditions,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client'
import {
  AgxDeposit,
  AgxDatePicker,
  AgxCoolingOffPeriod,
  AgxDropdown,
  AgxFinanceCondition,
  AgxRadioGroup,
  AgxSegmentPicker,
  AgxSpacer,
  AgxTextInput,
  AgxToggleSwitch,
  AgxTypography,
  isFinanceConditionValid,
  useToast,
} from '@urbanx/agx.ui-components2'
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps'
import { useExchangeTaskForm } from './useExchangeTaskForm'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { toDateOnlyString } from 'utils/date'
import ContractComplianceSpecialConditions from '../ContractCompliance/ContractComplianceSpecialConditions'

interface ExchangeTaskPageProps extends TaskModalPageProps {
  exchangeForm: ReturnType<typeof useExchangeTaskForm>
}

const isInformationTask = (taskType?: TaskCategoryTaskType) =>
  taskType === TaskCategoryTaskType.SolicitorExchangeInformation ||
  taskType === TaskCategoryTaskType.AgentExchangeInformation ||
  taskType === TaskCategoryTaskType.ContractExchangeInformation

const noCoolingOffReasonOptions = [
  {
    label: 'Waived - 66W provided',
    value: NoCoolingOffReason.Waived,
  },
  {
    label: 'Exempt from cooling off',
    value: NoCoolingOffReason.Exempt,
  },
]

const settlementModeSegments = [
  { title: '# days from...', value: 'fromContractDate' },
  { title: 'on..', value: 'specificDate' },
]

const settlementTimeUnitOptions = [
  { label: 'days', value: TimeUnit.Days },
  { label: 'weeks', value: TimeUnit.Weeks },
]

const settlementComponentWidth = '520px'
const settlementDaysInputWidth = '90px'
const settlementTimeUnitWidth = '164px'

const getSettlementMode = (
  settlementInfo: ReturnType<typeof useExchangeTaskForm>['state']['settlementInfo']
) =>
  settlementInfo?.condition === Condition.OnSpecificDate
    ? 'specificDate'
    : 'fromContractDate'

const getCoolingOffDetails = (
  state: ReturnType<typeof useExchangeTaskForm>['state']
): CoolingOffDetails => ({
  coolingOffType: state.coolingOffType,
  coolingOffDays: state.customCoolingOffDays,
  noCoolingOffReason: state.noCoolingOffReason,
})

const syncStep = (
  props: TaskModalPageProps,
  order: number,
  isCompleted: boolean
) => {
  const step = props.task.taskSteps?.find((candidate) => candidate.order === order)
  if (!step || step.isCompleted === isCompleted) return

  props.toggleTaskStep(order, isCompleted)
}

const ExchangeTaskPage = (props: ExchangeTaskPageProps) => {
  const taskType = props.task.category?.taskType

  if (isInformationTask(taskType)) {
    return <ExchangeInformationPage {...props} />
  }

  return <ExchangeContractDatesPage {...props} />
}

const ExchangeInformationPage = (props: ExchangeTaskPageProps) => {
  const { state, updateState } = props.exchangeForm
  const urbanXStep = props.task.taskSteps?.find(
    (candidate) => candidate.order === 1
  )
  const urbanXNoted = state.urbanXNoted ?? urbanXStep?.isCompleted ?? false
  const hasDepositValue =
    !!state.deposit?.isDepositSelected &&
    (!!state.deposit.percent || !!state.deposit.amount)
  const depositDefaultValue: Deposit = {
    ...state.deposit,
    depositType: DepositType.Initial,
    depositValueType: state.deposit?.depositValueType ?? ValueType.Percentage,
    isDepositSelected: state.deposit?.isDepositSelected ?? false,
  }

  const onDepositChanged = ({ value }: { id: string; value: Deposit }) => {
    if (!value.isDepositSelected && !state.deposit?.isDepositSelected) return

    updateState({
      deposit: {
        ...value,
        depositType: DepositType.Initial,
        isDepositSelected: value.isDepositSelected ?? true,
      },
    })
  }

  useEffect(() => {
    syncStep(props, 1, urbanXNoted)
    syncStep(props, 2, !!state.contractDate)
    syncStep(props, 3, !!state.salePrice && state.salePrice > 0)
    syncStep(props, 4, hasDepositValue)
    syncStep(props, 5, !!state.depositPayableOption)
  }, [
    hasDepositValue,
    props,
    state.contractDate,
    state.depositPayableOption,
    state.salePrice,
    urbanXNoted,
  ])

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-2xl">
      <AgxTypography
        id="exchange-information-title"
        type="label"
        size="large"
        color="var(--text-primary)"
      >
        Step {props.taskNumber}: Contract Information
      </AgxTypography>

      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
      >
        <AgxTypography id="urban-x-noted" type="body_ui" size="medium">
          Confirm URBAN X is noted on the Contract
        </AgxTypography>
        <AgxToggleSwitch
          id="urban-x-noted-toggle"
          checked={urbanXNoted}
          onChange={(urbanXNoted) => updateState({ urbanXNoted })}
        />
      </AgxSpacer>

      <AgxDatePicker
        id="exchange-contract-date"
        label="Contract Date"
        optionalText=""
        stretch
        value={
          state.contractDate ? dayjs(state.contractDate).format('YYYY-MM-DD') : ''
        }
        onChange={(value) =>
          updateState({ contractDate: value ? new Date(value) : undefined })
        }
      />

      <AgxTextInput
        id="exchange-sale-price"
        label="Sale Price"
        type="currency"
        stretch
        hideOptionalText
        addTrailingZeros={false}
        defaultValue={state.salePrice?.toString() ?? ''}
        onChange={(value) =>
          updateState({ salePrice: value ? Number(value) : undefined })
        }
      />

      <AgxDeposit
        id="exchange-deposit"
        label="Deposit"
        defaultValue={depositDefaultValue}
        onValueChanged={onDepositChanged}
        salePrice={state.salePrice ?? 0}
        showDepositDropdownOption={false}
        required
      />

      <AgxRadioGroup
        id="exchange-deposit-payable"
        label="Deposit Payable"
        layout="stacked"
        value={state.depositPayableOption}
        options={[
          { label: '.25% paid upfront', value: 'quarter' },
          { label: 'Full deposit paid upfront', value: 'full' },
        ]}
        onChange={(depositPayableSelection) =>
          updateState({
            depositPayableOption:
              depositPayableSelection === 'full' ? 'full' : 'quarter',
          })
        }
      />
    </AgxSpacer>
  )
}

const ExchangeContractDatesPage = (props: ExchangeTaskPageProps) => {
  const { state, updateState } = props.exchangeForm
  const contractId = props.task.contractId ?? ''
  const campaignId = props.campaign.campaignId ?? ''
  const { pushToast } = useToast()
  const hasHandledInitialSettlementSegment = useRef(false)
  const [localSpecialConditions, setLocalSpecialConditions] = useState<
    SpecialConditionData[]
  >([])

  const { data: specialConditionsResponse } = useStaffGetSpecialConditions(
    campaignId,
    contractId
  )
  const invalidateSpecialConditions = useInvalidateUseStaffGetSpecialConditions()
  const { staffUpdateContractSpecialConditions } =
    useStaffUpdateContractSpecialConditions({
      onSuccess: () => {},
      onError: () => {
        pushToast({
          type: 'error',
          message: 'Failed to update special conditions',
        })
      },
    })

  useEffect(() => {
    setLocalSpecialConditions(
      specialConditionsResponse?.specialConditionsData ?? []
    )
  }, [specialConditionsResponse?.specialConditionsData])

  useEffect(() => {
    const settlementMode = getSettlementMode(state.settlementInfo)
    const hasSettlement =
      settlementMode === 'specificDate'
        ? !!state.settlementInfo?.referenceDate
        : !!state.settlementInfo?.daysFromDate &&
          state.settlementInfo.daysFromDate > 0
    const hasFinanceCondition = isFinanceConditionValid(state.subjectToFinance)
    const hasCoolingOffDetails =
      !!state.coolingOffType &&
      (state.coolingOffType !== CoolingOffType.NDays ||
        (!!state.customCoolingOffDays && state.customCoolingOffDays > 0)) &&
      (state.coolingOffType !== CoolingOffType.NoCoolingOff ||
        !!state.noCoolingOffReason)

    syncStep(props, 1, hasCoolingOffDetails)
    syncStep(props, 2, hasFinanceCondition)
    syncStep(props, 3, true)
    syncStep(props, 4, hasSettlement)
  }, [
    props,
    state.customCoolingOffDays,
    state.coolingOffType,
    state.noCoolingOffReason,
    state.settlementInfo,
    state.subjectToFinance,
  ])

  const updateSpecialConditions = async (
    updatedConditions: SpecialConditionData[]
  ) => {
    setLocalSpecialConditions(updatedConditions)
    updateState({ specialConditionsReviewed: true })

    await staffUpdateContractSpecialConditions({
      contractId,
      body: updatedConditions,
    })
    await invalidateSpecialConditions(campaignId, contractId)
  }

  const onSettlementSelectionChange = (settlementSelection: string) => {
    if (!hasHandledInitialSettlementSegment.current) {
      hasHandledInitialSettlementSegment.current = true
      return
    }

    const nextSettlementSelection =
      settlementSelection === 'specificDate' ? 'specificDate' : 'fromContractDate'
    const currentSettlementSelection = getSettlementMode(state.settlementInfo)

    if (nextSettlementSelection === currentSettlementSelection) return

    updateState({
      settlementInfo:
        nextSettlementSelection === 'specificDate'
          ? {
              ...state.settlementInfo,
              condition: Condition.OnSpecificDate,
              daysFromDate: undefined,
              referenceDate: undefined,
            }
          : {
              ...state.settlementInfo,
              condition: Condition.NdaysFromDate,
              daysFromDate: undefined,
              referenceDate: undefined,
              fromDateTimeUnit:
                state.settlementInfo?.fromDateTimeUnit ?? TimeUnit.Days,
            },
    })
  }
  const settlementMode = getSettlementMode(state.settlementInfo)

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-2xl">
      <AgxTypography
        id="exchange-dates-title"
        type="label"
        size="large"
        color="var(--text-primary)"
      >
        Step {props.taskNumber}: Contract Dates
      </AgxTypography>

      <AgxCoolingOffPeriod
        id="exchange-cooling-off-period"
        label="Cooling Off Period"
        layout="stacked"
        defaultValue={getCoolingOffDetails(state)}
        options={[
          { label: '5 days', value: CoolingOffType.FiveDays },
          { label: '10 days', value: CoolingOffType.TenDays },
          { label: '___ days', value: CoolingOffType.NDays },
          { label: 'No cooling off period', value: CoolingOffType.NoCoolingOff },
        ]}
        noCoolingOffReasonLabel="Reason:"
        noCoolingOffReasonOptions={noCoolingOffReasonOptions}
        onValueChanged={({ value }) => {
          updateState({
            coolingOffType: value.coolingOffType,
            customCoolingOffDays: value.coolingOffDays,
            noCoolingOffReason: value.noCoolingOffReason,
          })
        }}
      />

      <AgxFinanceCondition
        id="exchange-finance"
        label="Finance"
        defaultValue={state.subjectToFinance}
        validate
        specialConditions={localSpecialConditions}
        campaignId={campaignId}
        defaultToNotSubjectToFinance={false}
        defaultSubjectToFinanceCondition={Condition.OnSpecificDate}
        onValueChanged={({ value }) => updateState({ subjectToFinance: value })}
      />

      <AgxSpacer
        direction="vertical"
        gap="spacing-xl"
        style={{ width: settlementComponentWidth, maxWidth: '100%' }}
      >
        <AgxTypography
          id="exchange-settlement-label"
          type="label"
          size="medium"
          color="var(--text-primary)"
        >
          Settlement Date
        </AgxTypography>

        <AgxSegmentPicker
          key={`exchange-settlement-mode-${settlementMode}`}
          id="exchange-settlement-mode"
          segments={settlementModeSegments}
          defaultValue={settlementMode}
          onChange={onSettlementSelectionChange}
        />

        {settlementMode === 'specificDate' ? (
          <AgxDatePicker
            id="exchange-settlement-date"
            noHeader
            stretch
            value={
              state.settlementInfo?.referenceDate
                ? dayjs(state.settlementInfo.referenceDate).format('YYYY-MM-DD')
                : ''
            }
            onChange={(value) =>
              updateState({
                settlementInfo: {
                  ...state.settlementInfo,
                  condition: Condition.OnSpecificDate,
                  referenceDate: toDateOnlyString(value),
                },
              })
            }
          />
        ) : (
          <AgxSpacer
            direction="horizontal"
            gap="xs"
            contentAlignment="baseline"
            style={{
              flexWrap: 'nowrap',
              width: 'max-content',
              maxWidth: '100%',
            }}
          >
            <AgxSpacer flexShrink={0} style={{ width: settlementDaysInputWidth }}>
              <AgxTextInput
                id="exchange-settlement-days"
                type="number"
                noHeader
                maxLength={3}
                stretch
                defaultValue={state.settlementInfo?.daysFromDate?.toString() ?? ''}
                onChange={(value) =>
                  updateState({
                    settlementInfo: {
                      ...state.settlementInfo,
                      condition: Condition.NdaysFromDate,
                      daysFromDate: value ? Number(value) : undefined,
                    },
                  })
                }
              />
            </AgxSpacer>
            <AgxSpacer flexShrink={0} style={{ width: settlementTimeUnitWidth }}>
              <AgxDropdown
                id="exchange-settlement-time-unit"
                options={settlementTimeUnitOptions}
                selected={
                  state.settlementInfo?.fromDateTimeUnit ?? TimeUnit.Days
                }
                noHeader
                onChange={(settlementTimeUnit) =>
                  updateState({
                    settlementInfo: {
                      ...state.settlementInfo,
                      condition: Condition.NdaysFromDate,
                      fromDateTimeUnit: settlementTimeUnit as TimeUnit,
                    },
                  })
                }
              />
            </AgxSpacer>
            <AgxTypography
              id="exchange-settlement-from-contract-date"
              type="body_ui"
              size="medium"
              color="var(--text-primary)"
              noWrap
              style={{ flexShrink: 0 }}
            >
              from Contract Date
            </AgxTypography>
          </AgxSpacer>
        )}
      </AgxSpacer>

      <AgxSpacer direction="vertical" fill gap="spacing-md">
        <ContractComplianceSpecialConditions
          {...props}
          addButtonVariant="primary"
          specialConditions={localSpecialConditions}
          onSpecialConditionsUpdated={updateSpecialConditions}
        />
      </AgxSpacer>
    </AgxSpacer>
  )
}

export default ExchangeTaskPage
